import { CommonModule, Location, UpperCasePipe } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoPipe } from '@jsverse/transloco';
import { TodayTimeOrDatePipe } from '@zero/shared/ui-pipes';
import { TradingSessionComponent } from '@zero/shared/ui-web-components';
import { ButtonComponent } from '@zero/shared/ui-web-design-system';
import { AbstractTradingService } from '@zero/shared/util-abstract-services';
import { BezugsrechtData } from '@zero/shared/util-models';
import { BehaviorSubject, Subject, filter } from 'rxjs';
import { BezugsrechtBackLinkComponent } from '../back-link/bezugsrecht-back-link.component';
import { BezugsrechtBase } from '../bezugsrecht.base';

@Component({
  imports: [
    CommonModule,
    TranslocoPipe,
    BezugsrechtBackLinkComponent,
    TradingSessionComponent,
    TodayTimeOrDatePipe,
    ButtonComponent,
    UpperCasePipe,
  ],
  templateUrl: './bezugsrecht-summary.component.html',
})
export class BezugsrechtSummaryComponent extends BezugsrechtBase implements OnInit {
  public sessionAuthorized: boolean;
  public showExternalExchangeHint: boolean;

  public lowTradingThreshold: number;
  public minimumQuantitySurcharge: number;
  public showLowTrading$: Subject<boolean>;
  public showLowTradingHint$: Subject<boolean>;
  public showDisproportionateFeesHint$: Subject<boolean>;
  public clicked: boolean;
  public showWarning = false;
  public warningAccepted = false;
  public calculationWarningValue: number;
  public valutatag: Date;

  private readonly _location = inject(Location);
  private readonly _tradingService = inject(AbstractTradingService);

  public override ngOnInit(): void {
    super.ngOnInit();

    this.init();
    this.handleRedirects();
  }

  private init(): void {
    this.service.bezugsrechtData$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      if (data.announcement?.bzrHandelbarBroker && (!data?.asset || !data?.directivePreview)) {
        return this.back();
      }

      this.initTradingService(data);
      this.showExternalExchangeHint =
        !data.directivePreview.bzrHandelbarBroker && data.directivePreview.bezugBzr + data.directivePreview.verkaufBzr > 0;

      const buyingPower = this.model.buyingPower;
      const salesBzr = this.model.directivePreview.verkaufBzrWertSumme;
      const buyShares = this.model.directivePreview.bezugAktienWertSumme;
      const buyBzr = this.model.directivePreview.bezugBzrWertSumme;
      this.calculationWarningValue = buyingPower + salesBzr - buyShares - buyBzr;
      this.valutatag = this.model.announcement.bezugTermin;

      this.showWarning = this.calculationWarningValue < 0;
    });
  }

  private handleRedirects(): void {
    this.service.bezugsrechtData$
      .pipe(
        filter((data) => !!data?.directive),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.router.navigateByUrl(`${this.rootUrl}/${this.BEZUGSRECHT_ROUTES.SUCCESS}`));
  }

  public sessionAuthState(value: boolean) {
    this.sessionAuthorized = value;
  }

  public override back = (): void => {
    if (this.model?.directivePreview) {
      this.model.directivePreview = null;
    }
    this._location.back();
  };

  public confirmLowTradingAndPlaceDirective() {
    this._tradingService.confirmLowTrading().then((success) => {
      if (success) {
        this.placeDirective();
      }
    });
  }

  public placeDirective(): void {
    this.msgService.clearAllMessages();

    if (!this.model?.directivePreview || !this.sessionAuthorized) {
      this.msgService.addErrorMessage(this.translateService.translate('bezugsrecht-service.error'));
      return;
    }

    const { announcement, directivePreview, buyableAmount } = this.model;
    this.service.placeDirective(announcement, directivePreview, buyableAmount);
  }

  private initTradingService(data: BezugsrechtData): void {
    if (
      !data?.asset ||
      !data?.directivePreview ||
      data.directivePreview.bezugBzr + data.directivePreview.verkaufBzr == 0 ||
      data.directivePreview.bezugBzrMindermengenzuschlag + data.directivePreview.verkaufBzrMindermengenzuschlag == 0
    ) {
      return;
    }
    this._tradingService.initAssetDependingValues(data.asset);
    this.lowTradingThreshold = this._tradingService.lowTradingThreshold;

    let sum: number;
    if (data.directivePreview.bezugBzr) {
      sum = data.directivePreview.bezugBzrWertSumme;
      this.minimumQuantitySurcharge = data.directivePreview.bezugBzrMindermengenzuschlag;
    } else {
      sum = data.directivePreview.verkaufBzrWertSumme;
      this.minimumQuantitySurcharge = data.directivePreview.verkaufBzrMindermengenzuschlag;
    }

    this.showLowTrading$ = new BehaviorSubject(this._tradingService.showLowTrading());
    this.showLowTradingHint$ = new BehaviorSubject(this._tradingService.showLowTradingHint(data.asset.instrumentType, sum));
    this.showDisproportionateFeesHint$ = new BehaviorSubject(this._tradingService.showDisproportionateFeesHint(sum));
  }

  public cancelWarning(): void {
    this.back();
  }

  public acceptWarning(): void {
    this.warningAccepted = true;
  }
}
